// This API queries the proxy middleware in ASP to directly get Drupal data
// The generated types allow parsing of the responses,
// which will then be transformed to match the requirements of the components
import { List, Map, Set } from 'immutable';
import * as Moment from 'moment';
import { getDrupalActivities } from '../../Shared/api/drupal/drupalApi';
import { placeholderStyleUrls } from '../../Shared/api/drupal/drupalTypes';
import { node_parser } from '../../Shared/api/drupal/parser';
import { lastClusterOfDigits } from '../../Shared/utils/string';
import { isFilterPeriod } from '../custom_components/components/region_and_date_filter';
import * as QueryContext from '../custom_components/odata_context';
import { moment } from '../custom_utils/moment';
import * as ConvertNodeTo from './drupal_node_conversion';
const odataBaseUrl = `/api/v1/OData/GetQuery?Query=`;
export const loadingText = "Bezig met laden...";
export const errorText = "De data kon niet worden geladen.";
export const noResultsText = "Er zijn geen resultaten gevonden.";
export const drupalUrl = "/api/drupal";
const previewUrl = "/api";
export const catchError = ()=>(e)=>{
        console.log(e);
        return "error";
    };
export const get = function(url) {
    let processor = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : (x)=>x;
    return fetch(url).then((x)=>x.status == 200 ? x.json() : Promise.reject(x.statusText)).then(processor).then((x)=>x).catch(catchError());
};
export const getDatesForPeriod = (period)=>{
    // For each FilterPeriod, define how many days the search period
    // starts from today, and ends from today
    const ranges = {
        'vandaag': [
            0,
            0
        ],
        'morgen': [
            1,
            1
        ],
        'komende-zeven-dagen': [
            0,
            7
        ],
        'komende-dertig-dagen': [
            0,
            30
        ],
        'komend-halfjaar': [
            0,
            182
        ]
    };
    // These are the search markers, that may move depending on the selected
    // FilterPeriod or Date
    const start = Moment().startOf('day') // start of today
    ;
    const end = Moment().endOf('day') // end of today
    ;
    // handle garbled input by defaulting to a half year
    let range = ranges['komend-halfjaar'];
    // handle period
    if (isFilterPeriod(period)) {
        range = ranges[period];
    }
    // handle specific date
    const date = Moment(period, 'DD-MM-YYYY');
    if (date.isValid()) {
        const diff = date.diff(start, 'days') // number of days between selected date and now
        ;
        range = [
            diff,
            diff
        ];
    }
    start.add(range[0], 'days') // potentially shift start to tomorrow
    .subtract(3, 'hour'); // create some margin to deal with lacking time component in database
    end.add(range[1], 'days') // extend the search into the selected period
    .add(3, 'hour'); // create some margin to deal with lacking time component in database
    return {
        start,
        end
    };
};
const constructUrl = (route, params)=>{
    const preview = new RegExp("^/node-preview/").test(route);
    if (preview) {
        return `${previewUrl}${route}${params}`;
    }
    return `${drupalUrl}${route}`;
};
export const getPage = (r, p)=>{
    return get(constructUrl(r, p)).then((x)=>{
        if (x == "loading" || x == "error") return "error";
        try {
            const n = node_parser(x);
            const p = n.bundle == "activity_clusters" ? ConvertNodeTo.activityClustersPage(n) : n.bundle == "activity_detail" ? ConvertNodeTo.activityDetailPage(n) : n.bundle == "blog_overview" ? ConvertNodeTo.blogOverviewPage(n) : n.bundle == "blog_post" ? ConvertNodeTo.blogPostPage(n) : n.bundle == "collection_overview" ? ConvertNodeTo.collectionPage(n) : n.bundle == "location_detail" ? ConvertNodeTo.locationDetailPage(n) : n.bundle == "news_item" ? ConvertNodeTo.newsItemPage(n) : n.bundle == "news_overview" ? ConvertNodeTo.newsOverviewPage(n) : n.bundle == "newsletter" ? ConvertNodeTo.newsletterSignupPage(n) : n.bundle == "home" ? ConvertNodeTo.homePage(n) : n.bundle == "report_overview" ? ConvertNodeTo.reportOverview(n) : n.bundle == "location" ? ConvertNodeTo.locationPage(n) : n.bundle == "location_overview" ? ConvertNodeTo.locationOverviewPage(n) : n.bundle == "faq" ? 'error' // This content type is not a page
             : n.bundle == 'event' // Events will be handled by the Events feature
             ? 'error' : ConvertNodeTo.generalContentPage(n);
            return p;
        } catch (e) {
            console.log(e);
            console.log("Are you sure all the required fields in Drupal are filled?");
        }
        return 'error';
    });
};
export const getRegions = ()=>{
    let regions = QueryContext.RegionQuery(odataBaseUrl).Select("Id", "Title", 'Slug').IncludeStrict("Region_Location", (q)=>q.Select("RegionId", "LocationId").IncludeStrict("Location", (q)=>q.Select("Id").IncludeStrict("Location_ActivityEvent", (q)=>q.Select("LocationId", "ActivityEventId").IncludeStrict("ActivityEvent", (q)=>q.Select("Id", "Date").Where((p)=>p.Date.IsAfter(new Date())))))).ToList();
    return regions.then((r)=>{
        try {
            return r.reduce((a, e)=>a.set(e.Title, {
                    id: e.Id,
                    slug: e.Slug,
                    name: e.Title,
                    locations: e.Region_Location.map((l)=>l.LocationId).toList()
                }), Map()).sort((a, b)=>a.slug.localeCompare(b.slug)).toOrderedMap();
        } catch (e) {
            console.log;
            return "error";
        }
    });
};
const selectWhere = (q, c)=>c(q);
// During aggregation the earliest date found is kept, hence the high initial Date
const initialEventAggregation = {
    date: new Date('9999'),
    locations: Set(),
    regions: Set()
};
export const getActivityClusters = (f)=>{
    let q = selectWhere(QueryContext.ActivityClusterQuery(odataBaseUrl).Select('Id', 'Title', 'Description', 'Slug'), (c)=>f.cluster.kind == 'some' ? f.cluster.value(c) : c).IncludeStrict('ActivityCluster_Activity', (q)=>q.Select('ActivityId', 'ActivityClusterId').IncludeStrict('Activity', (q)=>selectWhere(q.Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'), (a)=>f.activity.kind == "some" ? f.activity.value(a) : a).IncludeStrict('Activity_ActivityEditionN', (q)=>q.Select('ActivityId', 'ActivityEditionNId').IncludeStrict('ActivityEditionN', (q)=>selectWhere(q.Select('Id', 'SignupText', 'SignupURL'), (e)=>f.edition.kind == 'some' ? f.edition.value(e) : e).IncludeStrict('ActivityEditionN_ActivityEvent', (ae)=>ae.Select('ActivityEditionNId', 'ActivityEventId').IncludeStrict('ActivityEvent', (ev)=>selectWhere(ev.Select('Id', 'Date', 'Start', 'End'), (e)=>f.event.kind == 'some' ? f.event.value(e) : e).IncludeStrict('Location_ActivityEvent', (q)=>q.Select('LocationId', 'ActivityEventId').IncludeStrict('Location', (q)=>selectWhere(q.Select('Id', 'Title', 'Adres'), (q)=>f.location.kind == 'some' ? f.location.value(q) : q).IncludeStrict('Region_Location', (q)=>q.Select('LocationId', 'RegionId').IncludeStrict('Region', (q)=>selectWhere(q.Select('Id', 'Title', 'Slug'), (r)=>f.region.kind == 'some' ? f.region.value(r) : r))))))))))).ToList();
    //console.log("getActivityClusters", q)
    try {
        let result = q.then(async (data)=>{
            /** Activity IDs across all clusters */ const activityIds = data.flatMap((cluster)=>cluster.ActivityCluster_Activity.map((a_c)=>a_c.ActivityId));
            const drupalActivitiesData = await getDrupalActivities(activityIds);
            if (drupalActivitiesData == 'loading' || drupalActivitiesData == 'error' || drupalActivitiesData.length == 0) return List();
            /** Map from pdb ID to Drupal Activity */ const drupalActivities = drupalActivitiesData.reduce((acc, activity)=>acc.set(activity.product_db_id, activity), Map());
            let props = data.reduce((propsForClusters, cluster)=>{
                // Define some types to help later on
                const activityPrototype = cluster.ActivityCluster_Activity.first().Activity.first();
                const eventPrototype = activityPrototype.Activity_ActivityEditionN.first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first();
                /** Aggregate all Events associated with a ActivityCluster_Activity row */ const eventsPerActivity = (activity)=>activity.Activity_ActivityEditionN.flatMap((a_ae)=>a_ae.ActivityEditionN.flatMap((ae)=>ae.ActivityEditionN_ActivityEvent.flatMap((ed_ev)=>ed_ev.ActivityEvent)));
                const initialClusterSplit = {
                    stickyTips: List(),
                    stickies: List(),
                    regular: List()
                };
                const activities = cluster.ActivityCluster_Activity.flatMap((ac_a)=>ac_a.Activity);
                // Split all Activities into Tips, Stickies and regular Activities
                const { stickyTips, stickies, regular } = activities.reduce((acc, activity)=>{
                    let { stickyTips, stickies, regular } = acc;
                    if (activity.StickyTip) {
                        stickyTips = stickyTips.push(activity);
                    } else if (activity.Sticky) {
                        stickies = stickies.push(activity);
                    } else {
                        regular = regular.push(activity);
                    }
                    return {
                        stickyTips,
                        stickies,
                        regular
                    };
                }, initialClusterSplit);
                // Per list, sort all Activities by the earliest Event.Date associated with each Activity.
                // Then concatenate the resulting three sorted lists.
                const clusterActivities = List([
                    ...stickyTips.sortBy((activity)=>eventsPerActivity(activity).sortBy((event)=>event.Date).first().Date),
                    ...stickies.sortBy((activity)=>eventsPerActivity(activity).sortBy((event)=>event.Date).first().Date),
                    ...regular.sortBy((activity)=>eventsPerActivity(activity).sortBy((event)=>event.Date).first().Date)
                ]);
                let activitySummaries = clusterActivities.reduce((activityAccumulator, activity)=>{
                    var _locations_first, _locations_first1, _metadata_image;
                    // Skip activity if no Drupal counterpart was found
                    const metadata = drupalActivities.filter((dA)=>lastClusterOfDigits(dA.product_db_id) == activity.Id.toString()).first();
                    if (!metadata) return activityAccumulator;
                    // Aggregate all Events associated with this row of the results
                    const allEvents = eventsPerActivity(activity);
                    // Given a list of events, grab the dates and the unique locations and regions from them
                    const { date, locations, regions } = allEvents.reduce((acc, _event)=>{
                        const location = _event.Location_ActivityEvent.first().Location.first();
                        const region = location.Region_Location.first().Region.first();
                        return {
                            date: acc.date < _event.Date ? acc.date : _event.Date,
                            locations: acc.locations.add({
                                name: location.Title || "",
                                address: location.Adres || ""
                            }),
                            regions: acc.regions.add(region.Title)
                        };
                    }, initialEventAggregation);
                    if (regions.isEmpty()) return activityAccumulator;
                    return activityAccumulator.push({
                        headingLevel: 'h3',
                        title: metadata.title,
                        date: date,
                        location: locations.count() > 1 ? 'multiple' : ((_locations_first = locations.first()) === null || _locations_first === void 0 ? void 0 : _locations_first.name) || "",
                        address: locations.count() > 1 ? 'multiple' : ((_locations_first1 = locations.first()) === null || _locations_first1 === void 0 ? void 0 : _locations_first1.address) || "",
                        subtitle: metadata.description,
                        route: metadata.url,
                        imageAlt: metadata.image_alt,
                        styleUrls: ((_metadata_image = metadata.image) === null || _metadata_image === void 0 ? void 0 : _metadata_image.field_media_image.image.style_urls) || placeholderStyleUrls,
                        isTip: activity.StickyTip
                    });
                }, List());
                if (activitySummaries.isEmpty()) return propsForClusters;
                return propsForClusters.push({
                    title: cluster.Title,
                    slug: cluster.Slug,
                    description: cluster.Description,
                    activitySummaries
                });
            }, List()).sortBy((c)=>c.title.toLocaleLowerCase());
            return props;
        });
        return result;
    } catch (e) {
        console.log(e);
        return "error";
    }
};
export const getActivityEditions = (f)=>{
    let q = selectWhere(QueryContext.ActivityQuery(odataBaseUrl).Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'), (q)=>f.activity.kind == 'some' ? f.activity.value(q) : q).IncludeStrict('ActivityCluster_Activity', (q)=>q.Select('ActivityId', 'ActivityClusterId').IncludeStrict('ActivityCluster', (q)=>q.Select('Title'))).IncludeStrict('Activity_ActivityEditionN', (q)=>q.Select('ActivityId', 'ActivityEditionNId').IncludeStrict('ActivityEditionN', (q)=>selectWhere(q.Select('Id', 'SignupText', 'SignupURL'), (e)=>f.edition.kind == 'some' ? f.edition.value(e) : e).Include('ActivityEditionN_Book', (aeb)=>aeb.Select('ActivityEditionNId', 'BookId').Include('Book', (b)=>b.Select('Id', 'Name', 'Author', 'Image', 'URL'))).IncludeStrict('ActivityEditionN_ActivityEvent', (ae)=>ae.Select('ActivityEditionNId', 'ActivityEventId').IncludeStrict('ActivityEvent', (ev)=>selectWhere(ev.Select('Id', 'Date', 'Start', 'End'), (e)=>f.event.kind == 'some' ? f.event.value(e) : e).IncludeStrict('Location_ActivityEvent', (q)=>q.Select('LocationId', 'ActivityEventId').IncludeStrict('Location', (q)=>selectWhere(q.Select('Id', 'Title', 'Adres'), (q)=>f.location.kind == 'some' ? f.location.value(q) : q).IncludeStrict('Region_Location', (q)=>q.Select('LocationId', 'RegionId').IncludeStrict('Region', (q)=>selectWhere(q.Select('Id', 'Title', 'Slug'), (r)=>f.region.kind == 'some' ? f.region.value(r) : r))))))))).ToList();
    //console.log("getActivityEditions ", q)
    try {
        let result = q.then(async (data)=>!data.isEmpty() ? data.first().Activity_ActivityEditionN.reduce((l, a)=>{
                const clusterTitle = data.first().ActivityCluster_Activity.first().ActivityCluster.first().Title;
                let events = List();
                a.ActivityEditionN.first().ActivityEditionN_ActivityEvent.forEach((x)=>{
                    events = events.push({
                        id: x.ActivityEvent.first().Id,
                        date: x.ActivityEvent.first().Date,
                        start: x.ActivityEvent.first().Start,
                        end: x.ActivityEvent.first().End,
                        location: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Title,
                        region: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Region_Location.first().Region.first().Title,
                        address: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Adres
                    });
                });
                if (events.isEmpty()) return l;
                let activityBook = a.ActivityEditionN != null && a.ActivityEditionN.first() != null && a.ActivityEditionN.first().ActivityEditionN_Book != null && a.ActivityEditionN.first().ActivityEditionN_Book.first() != null && a.ActivityEditionN.first().ActivityEditionN_Book.first().Book != null ? a.ActivityEditionN.first().ActivityEditionN_Book.first().Book.first() : null;
                return l.push({
                    id: a.ActivityEditionN.first().Id,
                    signupText: a.ActivityEditionN.first().SignupText,
                    signupUrl: a.ActivityEditionN.first().SignupURL,
                    activityEvents: events,
                    book: activityBook != null ? {
                        title: activityBook.Name,
                        author: activityBook.Author,
                        id: activityBook.Id,
                        url: activityBook.URL,
                        image: activityBook.Image
                    } : null,
                    clusterTitle
                });
            }, List()) : List());
        //console.log("getActivityEditions ", result)
        return result;
    } catch (e) {
        console.log(e);
        return "error";
    }
};
export const getActivities = (f)=>{
    let q = selectWhere(QueryContext.ActivityQuery(odataBaseUrl).Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'), (q)=>f.activity.kind == 'some' ? f.activity.value(q) : q).IncludeStrict('Activity_ActivityEditionN', (q)=>q.Select('ActivityId', 'ActivityEditionNId').IncludeStrict('ActivityEditionN', (q)=>selectWhere(q.Select('Id', 'SignupText', 'SignupURL'), (e)=>f.edition.kind == 'some' ? f.edition.value(e) : e).IncludeStrict('ActivityEditionN_ActivityEvent', (ae)=>ae.Select('ActivityEditionNId', 'ActivityEventId').IncludeStrict('ActivityEvent', (ev)=>selectWhere(ev.Select('Id', 'Date', 'Start', 'End'), (e)=>f.event.kind == 'some' ? f.event.value(e) : e).IncludeStrict('Location_ActivityEvent', (q)=>q.Select('LocationId', 'ActivityEventId').IncludeStrict('Location', (q)=>selectWhere(q.Select('Id', 'Title', 'Adres'), (q)=>f.location.kind == 'some' ? f.location.value(q) : q).IncludeStrict('Region_Location', (q)=>q.Select('LocationId', 'RegionId').IncludeStrict('Region', (q)=>selectWhere(q.Select('Id', 'Title', 'Slug'), (r)=>f.region.kind == 'some' ? f.region.value(r) : r))))))))).ToList();
    try {
        let result = q.then(async (data)=>{
            let activityIds = data.reduce((l, a)=>l.push(a.Id), List());
            let drupalActivitiesData = await getDrupalActivities(activityIds);
            let drupalActivities = drupalActivitiesData != 'loading' && drupalActivitiesData != 'error' ? List(drupalActivitiesData) : List();
            // Sort activities by edition datetimes
            let activities = data.sort((a, b)=>a.Activity_ActivityEditionN.sort((c, d)=>c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime() - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime() - b.Activity_ActivityEditionN.sort((c, d)=>c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime() - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime());
            const stickyTips = activities.filter((v)=>v.StickyTip);
            const stickies = activities.filter((v)=>v.Sticky && !v.StickyTip);
            const regular = activities.filter((v)=>!v.Sticky && !v.StickyTip);
            // Put StickyTip's in front, then Sticky's, then non sticky without loosing sorted order.
            activities = List([
                ...stickyTips,
                ...stickies,
                ...regular
            ]);
            return activities.reduce((l, a)=>{
                var _metadata_image;
                let dates = a.Activity_ActivityEditionN.reduce((a, e)=>a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date), List()).sort((l, r)=>l.getTime() - r.getTime());
                let locations = a.Activity_ActivityEditionN.reduce((a, e)=>a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Location_ActivityEvent.first().Location.first().Title), List()).toSet();
                const metadata = drupalActivities.find((activity)=>Number(lastClusterOfDigits(activity.product_db_id)) == a.Id);
                if (!metadata) {
                    console.warn(`Eligible activity ${a.Title} (${a.Id}) was not found in Drupal`);
                    return l;
                }
                return l.push({
                    headingLevel: 'h4',
                    title: metadata.title,
                    subtitle: metadata.description,
                    location: locations.count() > 1 ? 'multiple' : locations.first(),
                    address: "",
                    date: dates.count() > 1 ? 'multiple' : dates.count() == 1 ? dates.first() : 'multiple',
                    imageAlt: metadata.image_alt,
                    styleUrls: ((_metadata_image = metadata.image) === null || _metadata_image === void 0 ? void 0 : _metadata_image.field_media_image.image.style_urls) || placeholderStyleUrls,
                    route: metadata.url,
                    isTip: a.StickyTip
                });
            }, List());
        });
        return result;
    } catch (e) {
        console.log(e);
        return "error";
    }
};
export const getNavigation = (id)=>get(`${drupalUrl}/api/menu_items/${id}`);
export const getDrupalVacancies = (categoryIds)=>get(`${drupalUrl}/api/vacancies/${categoryIds.join("+")}`);
export const getDrupalLocations = (pdbIds)=>get(`${drupalUrl}/api/locations/${pdbIds.join("+")}`);
export const getDrupalReports = ()=>get(`${drupalUrl}/api/reports`);
export const getReports = ()=>new Promise(async (res)=>{
        let drupalReports = await getDrupalReports();
        let reports = Map();
        if (drupalReports != 'error' && drupalReports != 'loading') {
            drupalReports.forEach((r)=>{
                if (!reports.has(r.id)) {
                    reports = reports.set(r.id, {
                        created: r.created,
                        datetimes: Set(),
                        imageAlt: r.imageAlt,
                        imageUrl: r.imageUrl,
                        imageUrlFallback: r.imageUrlFallback,
                        imageUrlLarge: '',
                        locations: Set(),
                        shortDescription: r.shortDescription,
                        title: r.title,
                        url: r.url
                    });
                }
                reports = reports.set(r.id, {
                    ...reports.get(r.id),
                    datetimes: r.datetime != "" ? reports.get(r.id).datetimes.add(moment.unix(parseInt(r.datetime))) : reports.get(r.id).datetimes,
                    locations: r.location != "" ? reports.get(r.id).locations.add(r.location) : reports.get(r.id).locations
                });
            });
            reports = reports.sort((a, b)=>{
                if (a.datetimes.isEmpty() || b.datetimes.isEmpty()) return 0;
                return b.datetimes.first().diff(a.datetimes.first());
            }).toOrderedMap();
            res(reports);
        } else {
            res('error');
        }
    });
export const getDrupalSearchImage = async ()=>{
    const response = await get(`${drupalUrl}/api/config-page/search-block-image`);
    if (response == 'error' || response == 'loading') return response;
    if (response.length == 0) return '';
    return response[0].src;
};
